/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, Observable, of, combineLatest, merge, from } from 'rxjs';
import {
	map,
	switchMap,
	takeUntil,
	finalize,
	scan,
	distinctUntilChanged,
	withLatestFrom,
	shareReplay,
	skip,
	filter,
	tap,
} from 'rxjs/operators';
import { Organization } from 'src/app/app-data/models/Organization';
import { OrganizationProject } from 'src/app/app-data/models/OrganizationProject';
import { DefaultSplitDataRenderType } from '@touchplan/common';
import { User } from '@touchplan/common';
import { OrganizationMembersApi } from 'src/app/app-data/state/OrganizationMembersApi';
import { OrganizationProjectsApi } from 'src/app/app-data/state/OrganizationProjectsApi';
import { OrganizationsApi } from 'src/app/app-data/state/OrganizationsApi';
import { AppState, AppStateUser } from 'src/app/services/app-state.service';
import {
	finalizeWithValue,
	subjectSubscribeWithoutComplete,
} from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	IScheduleDisclaimerData,
	UserPreferenceCategories,
} from '@touchplan/fancy-referencer';
import { FancyFirebaseOrganizationMembersApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-organization-members-api-builder.service';
import { FancyFirebaseOrganizationProjectsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-organization-projects-api-builder';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { ListLike } from 'src/app/app-data/interfaces/list-like';
import { FancyFirebaseOrganizationsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-organizations-api-builder.service';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { includeChangesInSubListMonitoring } from 'src/app/common/fancy-firebase/base/monitorSubList';
import { FirebaseListFactory } from 'src/app/common/fancy-firebase/services/factories';
import { OrganizationList } from 'src/app/common/fancy-firebase/lists/OrganizationList';
import { DataSnapshot } from '@angular/fire/compat/database/interfaces';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { OrganizationFirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/organization-cloud-functions.service';
import dayjs from 'dayjs';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import firebase from 'firebase/compat/app';
import { HttpClient } from '@angular/common/http';
import { GET_PROJECT_ORGANIZATIONS_ROUTE } from 'src/app/services/rest-api-configuration/rest-api-routes';

export enum RequestState {
	None,
	WaitingForServer,
	Success,
	Error,
}

export interface IProjectOrganization {
	name: string;
	organizationId: string;
	parentOrganizationId?: string;
	requiresScheduleDisclaimerAcknowledgment?: boolean;
}

export interface IProjectOrganizationsResponse {
	organizations: IProjectOrganization[];
}
/**
 * The OrganizationService is responsible for handling state for the organization's applications
 * and APIs. This service will also call the cloud functions to save api settings.
 * @example
 * ```ts
 * // Change the current application by using any of the applications listed in the applicationList$ property.
 * this.organizationService.applicationList$.subscribe(apps => {
 *   logger.log('application list', apps);
 *   logger.log('setting app 1');
 *   this.organizationService.application$.next(apps[0]);
 *   logger.log('setting app 2');
 *   this.organizationService.application$.next(apps[1]);
 * });
 *
 * // Get application's projects
 * this.organizationService.applicationProjects$.subscribe(value => {
 *   logger.log('projects: ', value);
 * });
 *
 * // Get API user
 * this.organizationService.applicationUser$.subscribe(apiUser => {
 *   logger.log('apiUser:', apiUser);
 * });
 * ```
 */
@Injectable({
	providedIn: 'root',
})
export class OrganizationService implements OnDestroy {
	private shutdownSubject$ = new Subject<void>();

	private logger: ILoggerService;

	// TODO: Top level org
	public organizationId$ = new BehaviorSubject<string>(null);
	public organization$: Subject<Organization>; // pointed at top organization

	// Top organization level APIs
	public organizationsApi: OrganizationsApi; // pointed at filtered list with org's applications
	public organizationMembersApi: OrganizationMembersApi; // pointed at list with current application members
	public organizationProjectsApi: OrganizationProjectsApi; // pointed to organization's projects

	/**
	 * All projects on this organization
	 */
	public allProjectsList$: Observable<Map<string, OrganizationProject>>;
	/**
	 * All applications on this organization
	 */
	public applicationList$: Observable<Map<string, Organization>>;

	// State
	private applicationUserSubject$ = new BehaviorSubject<User>(null);
	private applicationProjectsSubject$ = new BehaviorSubject<Map<string, OrganizationProject>>(null);

	/**
	 * Set this application to any of the applicationList to change the application, applicationUser and applicationProjects
	 */
	private applicationSubject$ = new BehaviorSubject<Organization>(null);
	private applicationChangeOnly$ = new BehaviorSubject<Organization>(null);

	/** Like [[this.application$]], but emits only on change. Prefer [[this.application$]] in most cases */
	public applicationChange$ = this.applicationChangeOnly$.asObservable();

	/** Emits on any change to the selected org/ application. Data change, unselecting, and selecting a new one */
	public application$ = this.applicationSubject$.asObservable();
	private applicationSync: Organization;

	/**
	 * Yields true or false, to indicate whether the currently selected org/app is being added (i.e. not yet saved) or not
	 */
	public isAdding$: Observable<boolean>;
	/**
	 * Yields true or false, to indicate whether there is a currently selected org/app
	 */
	public isEditing$: Observable<boolean>;

	/**
	 * Current application's api user
	 */
	public applicationUser$ = this.applicationUserSubject$.asObservable();

	/**
	 * Current application's projects
	 */
	public applicationProjects$ = this.applicationProjectsSubject$.asObservable();

	// Events
	/**
	 * This observable emits true when the service is waiting for a cloud function to finish, and emits false when it's no longer waiting.
	 */
	public requestState$ = new Subject<RequestState>();
	/**
	 * This observable emits when a new app secret is generated, with the new secret.
	 */
	public appSecretGenerated$ = new Subject<string>();

	private isOrgAdminSubject$ = new BehaviorSubject<boolean>(null);
	public isOrgAdmin$ = this.isOrgAdminSubject$.asObservable();

	constructor(
		private fancyFirebaseOrganizationMembersApiBuilder: FancyFirebaseOrganizationMembersApiBuilder,
		private fancyFirebaseOrganizationProjectsApiBuilder: FancyFirebaseOrganizationProjectsApiBuilder,
		private fancyFirebaseOrganizationsApiBuilder: FancyFirebaseOrganizationsApiBuilder,
		private firebaseListFactory: FirebaseListFactory,
		private appState: AppState,
		private organizationFBCloudFunctionsService: OrganizationFirebaseCloudFunctionsService,
		private featureFlagManager: FeatureFlagManager,
		private httpClient: HttpClient
	) {
		this.logger = LoggerServiceFactory.create('OrganizationService');
		const currentOrgApi$ = this.monitorOrgApi$(this.appState.user);

		//sets sync state
		currentOrgApi$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((orgApi) => (this.organizationsApi = orgApi));

		//detect the org with no parent id, proclaims it as the parent, emits
		const orgId$ = currentOrgApi$.pipe(
			switchMap((orgApi) => (orgApi ? orgApi.data.list$ : of(new Map()))),
			map((orgList) => {
				let parentOrgId: string | null = null;
				orgList.forEach((org) => {
					if (!org.view.parentOrganizationId) {
						parentOrgId = org.$id;
					}
				});
				return parentOrgId;
			}),
			distinctUntilChanged()
		);
		orgId$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(subjectSubscribeWithoutComplete(this.organizationId$));

		this.organizationId$
			.pipe(withLatestFrom(currentOrgApi$), takeUntil(this.shutdownSubject$))
			.subscribe(([orgId, orgApi]) => this.setupData(orgId, orgApi, orgId$.pipe(skip(1))));

		this.setupCurrentApplicationDescendents();
	}

	/**
	 * emits an orgApi instance based on appState.user. Automatically creates and destroys said api
	 * forwards nulls
	 */
	private monitorOrgApi$(user$: Observable<AppStateUser>) {
		return user$.pipe(
			distinctUntilChanged((prevUser, currUser) => prevUser?.$id === currUser?.$id),
			scan((lastOrgApi: OrganizationsApi, currentUserData: AppStateUser) => {
				if (lastOrgApi) {
					lastOrgApi.destroy();
				}
				if (!currentUserData) {
					return null;
				}
				return this.fancyFirebaseOrganizationsApiBuilder.getOrganizationsApi(currentUserData.$id);
			}, null),
			finalizeWithValue((lastOrgApi) => lastOrgApi?.destroy()),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	/**
	 * Setup some utility observables/ data based on the long running application$
	 */
	private setupCurrentApplicationDescendents() {
		//maintain applicationSync for the life of this service
		this.application$.pipe(takeUntil(this.shutdownSubject$)).subscribe((application) => {
			this.applicationSync = application;
		});

		this.isAdding$ = this.application$.pipe(
			map((app) => !!app?.localDataNotYetPersisted),
			takeUntil(this.shutdownSubject$)
		);

		this.isEditing$ = this.application$.pipe(
			map((app) => !!app),
			takeUntil(this.shutdownSubject$)
		);
	}

	private setupData(
		organizationId: string,
		organizationsApi: OrganizationsApi,
		destroyNotifier$: Observable<any>
	) {
		// Destroy things
		this.organizationMembersApi?.destroy();
		this.organizationProjectsApi?.destroy();

		if (!organizationId || !organizationsApi) {
			return;
		}

		// use to unsub automatically when either the entire service is destroyed or when the parent orgId changes
		const combinedDestroy$ = merge(this.shutdownSubject$, destroyNotifier$);

		this.organizationMembersApi = this.fancyFirebaseOrganizationMembersApiBuilder.getOrganizationMembersApi(
			organizationId,
			organizationsApi
		);
		this.organizationProjectsApi =
			this.fancyFirebaseOrganizationProjectsApiBuilder.getOrganizationProjectsApi(organizationId);

		// Actively listens if user is organization admin
		combineLatest([
			this.featureFlagManager.getFlag$(DynamicFlagTypes.ORGANIZATION_ENABLED),
			this.organizationMembersApi.data.list$,
		])
			.pipe(
				map(
					([isOrgEnabled, list]) =>
						isOrgEnabled &&
						list &&
						[...list.values()].some(
							(organizationMember) => organizationMember.view.orgAccessLevel === 'Admin'
						)
				),
				takeUntil(combinedDestroy$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.isOrgAdminSubject$));

		// Organization applications
		this.applicationList$ = organizationsApi.data.list$.pipe(
			// return new map that doesn't have parent
			map((list) => {
				const filteredList: typeof list = new Map();
				list.forEach((org, orgId) => {
					if (org.view.parentOrganizationId) {
						filteredList.set(orgId, org);
					}
				});
				return filteredList;
			})
		);

		// Organization projects
		this.allProjectsList$ = this.organizationProjectsApi.data.list$.asObservable();

		//utility thing used by the application observables
		const useFirstValue = (orgMap: Map<string, Organization>) => orgMap.values().next().value;

		// Selected application observables
		organizationsApi.data.editList$
			.pipe(
				map((orgMap) => useFirstValue(orgMap)),
				takeUntil(combinedDestroy$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.applicationChangeOnly$));

		organizationsApi.data.editList$
			.pipe(
				includeChangesInSubListMonitoring(organizationsApi.data.rawEvent$),
				map((orgMap) => useFirstValue(orgMap)),
				takeUntil(combinedDestroy$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.applicationSubject$));

		// App user
		this.applicationChangeOnly$
			.pipe(
				switchMap((app) => {
					if (app) {
						const appMembersApi = this.fancyFirebaseOrganizationMembersApiBuilder.getOrganizationMembersApi(
							app.$id,
							organizationsApi
						);
						return appMembersApi.data.list$.pipe(
							//the first user with isApi set (assumes) there can only be one
							map((list) => {
								for (const [key, member] of list) {
									if (member.view?.user?.isApi) {
										return member.view.user;
									}
								}
								return null;
							}),
							finalize(() => appMembersApi.destroy())
						);
					} else {
						return of(null);
					}
				}),
				distinctUntilChanged(),
				takeUntil(combinedDestroy$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.applicationUserSubject$));

		// App projects
		const applicationProject$ = this.applicationChangeOnly$.pipe(
			switchMap((app) => {
				if (app) {
					const appProjectsApi = this.fancyFirebaseOrganizationProjectsApiBuilder.getOrganizationProjectsApi(
						app.$id
					);
					return appProjectsApi.data.loadedList$.pipe(finalize(() => appProjectsApi.destroy()));
				} else {
					return of(null as Map<string, OrganizationProject>);
				}
			}),
			takeUntil(combinedDestroy$)
		);
		applicationProject$.subscribe(subjectSubscribeWithoutComplete(this.applicationProjectsSubject$));

		const sizeCheck = (a: Map<string, OrganizationProject>, b: Map<string, OrganizationProject>) => {
			if (!a && !b) {
				return false;
			}
			if (!a || !b) {
				return true;
			}
			return a.size !== b.size;
		};

		//apply project list data to the selected item (can't use the behaviorSubjects as we need to know when the first "real" event fires)
		this.applicationChangeOnly$
			.pipe(
				switchMap(() => {
					//probably add a distinctUntilChanged to each source here based around the size
					return combineLatest([
						this.organizationProjectsApi.data.loadedList$.pipe(distinctUntilChanged(sizeCheck)),
						applicationProject$.pipe(
							filter((x) => !!x),
							distinctUntilChanged(sizeCheck)
						),
					]).pipe(
						scan(
							(
								acc: [Map<string, OrganizationProject>, Map<string, OrganizationProject>, number],
								val: [Map<string, OrganizationProject>, Map<string, OrganizationProject>]
							) => {
								return [val[0], val[1], acc ? acc[2] + 1 : 0] as [
									Map<string, OrganizationProject>,
									Map<string, OrganizationProject>,
									number
								];
							},
							[null, null, -1]
						)
					);
				}),
				map(([allProjects, applicationProjects, count]) => {
					const result = Array.from(allProjects.entries(), ([projectId, project]) => {
						return { orgProject: project, selected: applicationProjects.has(projectId) };
					});
					return { projects: result, emissionNumber: count };
				}),
				takeUntil(combinedDestroy$)
			)
			.subscribe((result) => {
				const selectedOrg = this.applicationSync;
				// the first emission means that it's loaded and the "edit view" needs to be updated
				if (result.emissionNumber === 0) {
					this.organizationsApi.localUpdate(
						selectedOrg.$id,
						{ selectedProjects: result.projects },
						DefaultSplitDataRenderType.edit
					);
					this.organizationsApi.setSelectionDataLoaded(selectedOrg);
				}
				// just update the database source
				this.organizationsApi.localUpdate(
					selectedOrg.$id,
					{ selectedProjects: result.projects },
					DefaultSplitDataRenderType.database
				);
			});
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		this.organizationMembersApi?.destroy();
		this.organizationProjectsApi?.destroy();
	}

	public addApplication() {
		this.discard();
		// clear application to null value
		this.organizationsApi.addItem({
			parentOrganizationId: this.organizationId$.getValue(), //needs this to show up
			name: '',
			description: '',
			url: '',
		});
	}

	public editApplication(organization: Organization) {
		this.discard();
		this.organizationsApi.changeView(organization, DefaultSplitDataRenderType.edit);
	}

	public updateApplication(field: string, value: string) {
		if (!this.applicationSync) {
			return;
		}
		this.organizationsApi.localUpdate(this.applicationSync.$id, { [field]: value });
	}

	public removeUnsavedApplication(list: ListLike<Organization> | Organization, additionalArgs?: object) {
		if (list) {
			this.organizationsApi.removeItem(list, additionalArgs);
		}
	}

	public deleteApplication() {
		if (!this.applicationSync) {
			return;
		}
		if (this.applicationSync.localDataNotYetPersisted) {
			this.organizationsApi.removeItem(this.applicationSync);
		} else {
			//get a reference in case it needs to be "undeleted"
			const deletedApp = this.applicationSync;

			//actually delete (could do just write this directly in theory, but consistency? and persists for this data are disabled)
			this.organizationFBCloudFunctionsService.deleteOrganization(this.applicationSync.$id).subscribe({
				error: (e) => {
					//this should never realistically fire. But if it does somehow, it's less broken to do this than to not do it
					// if it threw an error and did not write anything, then successful recovery
					// if it threw an error and did write the key, then the item will get deleted in a moment anyways
					if (this.organizationsApi?.data?.syncList?.has(deletedApp.$id)) {
						this.organizationsApi.localUpdate(deletedApp.$id, { deletedAt: null });
					}

					this.logger.error('Could not delete organization ' + this.applicationSync.$id + '. ' + e.message);
				},
			});

			//pretend we did it immediately
			this.discard();
			this.organizationsApi.localUpdate(deletedApp.$id, { deletedAt: fbdbTime.syncTime });
		}
	}

	/**
	 * Changes the application's secret via rest api request. The observable appSecretGenerated will be called.
	 */
	public generateSecret() {
		if (!this.applicationSync) {
			return;
		}
		this.requestState$.next(RequestState.WaitingForServer);
		this.organizationFBCloudFunctionsService.changeOrganizationSecret(this.applicationSync.$id).subscribe({
			next: (newSecret) => {
				this.appSecretGenerated$.next(newSecret);
				this.requestState$.next(RequestState.Success);
			},
			error: (e) => {
				this.requestState$.next(RequestState.Error);
				this.logger.error('Could not change organization secret. ' + e.message);
			},
		});
	}

	public changeOrganizationProjects(projectIds: { [key: string]: boolean } | null) {
		if (!this.applicationSync) {
			return;
		}
		if (!projectIds) {
			this.organizationsApi.localUpdate(this.applicationSync.$id, { inheritProjects: true });
		} else {
			// make a cache for conciseness
			const currentProjects = new Map(
				this.applicationSync.view.selectedProjects.map((p) => [p.orgProject.$id, p.orgProject])
			);
			// bit of doctoring to convert this into the selectedProjectFormat
			const dataInfusedProjects: Array<{ orgProject: OrganizationProject; selected: boolean }> = Array.from(
				Object.entries(projectIds),
				([id, enabled]) => {
					return { selected: enabled, orgProject: currentProjects.get(id) };
				}
			);
			this.organizationsApi.localUpdate(this.applicationSync.$id, {
				inheritProjects: false,
				selectedProjects: dataInfusedProjects,
			});
		}
	}

	public save() {
		if (this.applicationSync === null) {
			return;
		}
		// At this point, "applicationSync" has:
		//   - currentRenderType = 'edit'
		//   - primaryType = 'database'
		// Also, at this time, changes are in the 'edit' view but not in the 'database' view.
		// We want to get the 'edit'-view's values, so that's why we need to pass that render-type here:
		const appView = this.applicationSync.generateDatabaseOutput(DefaultSplitDataRenderType.edit);
		// projectIds are null if inherited, otherwise it is a list of selected projectIds.
		const projectIds = appView.inheritProjects ? null : appView.projectIds;
		const { name, description, url } = this.applicationSync.generateDatabaseOutput(
			DefaultSplitDataRenderType.edit
		);
		this.logger.log('saving projectIds', projectIds);
		this.requestState$.next(RequestState.WaitingForServer);
		if (this.applicationSync.localDataNotYetPersisted) {
			const newAppId: string = this.applicationSync.$id;
			this.organizationFBCloudFunctionsService
				.createOrganization(newAppId, name, description, url, this.organizationId$.value, projectIds)
				.subscribe({
					next: (result) => {
						const appSecret = result.password;
						// We override the handling of the "persistData" event in OrganizationList, specifically so that:
						//    1. "localDataNotYetPersisted" is set to false; and
						//    2. data is otherwise NOT written to Firebase as normal, since "createOrganization" does so
						this.organizationsApi.persistData(this.applicationSync);
						// [sic] We need to copy the "applicationSync" handle because the first "changeView" call here leads to
						// a "this.application$" emission which overwrites "applicationSync".  (LOLcringe)
						// Hub-boy...  Kurtis ain't crazy about this whole "applicationSync" thing...  :-D
						const preservedAppSync = this.applicationSync;
						// The write succeeded, so we need to update the 'database' view:
						this.organizationsApi.changeView(preservedAppSync, DefaultSplitDataRenderType.database);
						// ...but we need to remain in 'edit' mode:
						this.organizationsApi.changeView(preservedAppSync, DefaultSplitDataRenderType.edit);

						this.appSecretGenerated$.next(appSecret);
						this.requestState$.next(RequestState.Success);
					},
					error: (e) => {
						this.requestState$.next(RequestState.Error);
						this.logger.log('Error creating organization. ' + e.message);
					},
				});
		} else {
			this.organizationFBCloudFunctionsService
				.changeOrganization(this.applicationSync.$id, name, description, url, projectIds)
				.subscribe({
					next: () => {
						// PR note:  removing the following line (i.e. commenting out for now) because if it executes,
						// the UI de-selects the application which was being edited and which was just saved.
						//
						//this.organizationsApi.changeView(this.applicationSync, DefaultSplitDataRenderType.database);
						this.requestState$.next(RequestState.Success);
						this.logger.log('changing organization successful!');
					},
					error: (e) => {
						this.requestState$.next(RequestState.Error);
						this.logger.log('Error updating organization. ' + e.message);
					},
				});
		}
	}

	public discard() {
		if (this.applicationSync) {
			this.organizationsApi.abortView(this.applicationSync, DefaultSplitDataRenderType.database);
		}
	}

	public cancel() {
		if (this.applicationSync?.localDataNotYetPersisted) {
			this.deleteApplication();
		} else {
			this.discard();
		}
	}

	/**
	 * Retrieves the parent organizations of a given project.
	 *
	 * @param projectId - The unique identifier of the project.
	 * @returns An observable that emits an array of parent organizations (`IProjectOrganization[]`).
	 *
	 * The method fetches the organizations associated with the specified project
	 * and filters out those that do not have a `parentOrganizationId`.
	 * NOTE: This is not a live list of organizations since we don't currently expect this data to change too frequently.
	 */
	public getProjectParentOrganizations$(projectId: string): Observable<IProjectOrganization[]> {
		return this.httpClient
			.get<IProjectOrganizationsResponse>(GET_PROJECT_ORGANIZATIONS_ROUTE(projectId))
			.pipe(
				map(({ organizations }) => {
					// In theory, there should only be one parent organization, but proactively checking for multiple in case that happens to be a thing
					const parentOrgs = organizations.filter((org) => !org.parentOrganizationId);
					return parentOrgs;
				})
			);
	}

	private isOrgDisclaimerAcknowledgementRequired$(projectId: string): Observable<boolean> {
		return this.httpClient
			.get<IProjectOrganizationsResponse>(GET_PROJECT_ORGANIZATIONS_ROUTE(projectId))
			.pipe(
				map(({ organizations }) => {
					// In theory, there should only be one parent organization, but proactively checking for multiple in case that happens to be a thing
					const parentOrgs = organizations.filter((org) => !org.parentOrganizationId);
					return parentOrgs.some((org) => !!org.requiresScheduleDisclaimerAcknowledgment);
				})
			);
	}

	private getUserDisclaimerRef(projectId: string, userId: string): firebase.database.Reference {
		return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			fancyFirebaseGetters.getProjectUserPreferencesPath(
				projectId,
				userId,
				UserPreferenceCategories.ScheduleDisclaimer
			),
			projectId
		);
	}

	public getIsOrgScheduleDisclaimerNeededAndExpired$(projectId: string, userId: string): Observable<boolean> {
		return this.appState.user.asObservable().pipe(
			switchMap((user) => {
				if (!!user.morphedUser) {
					return of(false);
				}
				return this.isOrgDisclaimerAcknowledgementRequired$(projectId);
			}),
			switchMap((isOrgDisclaimerAcknowledgementRequired) => {
				if (!isOrgDisclaimerAcknowledgementRequired) {
					return of(false);
				}
				const scheduleDisclaimerPreferenceRef = this.getUserDisclaimerRef(projectId, userId);
				return from(scheduleDisclaimerPreferenceRef.once('value')).pipe(
					map((snapshot) => {
						const syncTimeDate = dayjs(fbdbTime.syncTime);
						const scheduleDisclaimerPreference: IScheduleDisclaimerData = snapshot.val();
						const lastUpdatedAt = scheduleDisclaimerPreference?.lastUpdatedAt;
						if (!lastUpdatedAt) {
							// If there is no lastUpdatedAt value, we consider it expired
							return true;
						}
						const lastUpdatedAtDate = dayjs(lastUpdatedAt);
						// If the lastUpdatedAt value is in the past by 1 day (date, not time), we consider it expired
						return lastUpdatedAtDate.isBefore(syncTimeDate, 'D');
					})
				);
			})
		);
	}

	/**
	 * Updates the schedule disclaimer preferences for a user in a specific project.
	 *
	 * This method updates the `lastUpdatedAt` field to the current date. If the `createdAt` field
	 * is not already set, it will also set the `createdAt` field to the current date.
	 *
	 * @param projectId - The ID of the project.
	 * @param userId - The ID of the user.
	 * @returns An Observable that completes when the update is done.
	 */
	public updateUserOrgsScheduleDisclaimer$(
		projectId: string,
		userId: string,
		planId: string
	): Observable<void> {
		// Update the lastUpdatedAt and createdAt (if needed) value to the current date
		const scheduleDisclaimerPreferenceRef = this.getUserDisclaimerRef(projectId, userId);
		return from(scheduleDisclaimerPreferenceRef.once('value')).pipe(
			switchMap((snapshot) => {
				const scheduleDisclaimerPreference: IScheduleDisclaimerData = snapshot.val();
				const createdAt = scheduleDisclaimerPreference?.createdAt;
				const updateData = { lastUpdatedAt: fbdbTime.syncTime };
				if (!createdAt) {
					updateData['createdAt'] = fbdbTime.syncTime;
				}
				return from(scheduleDisclaimerPreferenceRef.update(updateData)).pipe(
					tap(() => {
						const orgId = this.organizationId$.value;
						analyticsEventsService.organizationScheduleDisclaimerAccepted(
							orgId,
							planId,
							userId,
							updateData.lastUpdatedAt
						);
					})
				);
			})
		);
	}
}
